/**
 * 用户控制台唯一的路由元数据表（2026-10-07）
 *
 * 侧边栏菜单、选中/展开状态、面包屑、Cmd+K 命令面板全部从这里派生，避免再次各自漂移。
 * 新增页面：在 NAV_SECTIONS（有菜单入口）或 EXTRA_PAGES（无菜单入口的详情/购买页）里加一行即可。
 * 路径均为相对路径（不含 userPath 前缀）。
 */
import React from 'react';
import {
    HomeOutlined, CloudServerOutlined, DatabaseOutlined, HddOutlined,
    ThunderboltOutlined, GlobalOutlined, ApartmentOutlined,
    SafetyCertificateOutlined, MailOutlined, CodeSandboxOutlined, SendOutlined, RobotOutlined, CloudOutlined, WalletOutlined, SettingOutlined, SwapOutlined,
    CustomerServiceOutlined, InboxOutlined, LayoutOutlined,
} from '@ant-design/icons';
import { t } from '../shared/i18n';

/**
 * 侧边栏：分区（title = 分组小标题）→ 产品（leaf 或带 children 的子菜单）。
 * leaf: { path, label, icon?, href?(外链), keywords? }
 */
export const NAV_SECTIONS = [
    {
        key: 'overview',
        items: [
            { path: '/dashboard', label: '概览', icon: <HomeOutlined />, keywords: ['home', 'dashboard', '首页', '控制台'] },
        ],
    },
    {
        key: 'compute',
        title: '计算',
        items: [
            {
                key: 'evm', label: '云服务器', icon: <CloudServerOutlined />,
                children: [
                    { path: '/evm/vms', label: '实例', keywords: ['vm', 'ecs', 'instance', '虚拟机', '云主机'] },
                    { path: '/evm/vds', label: 'VDS', keywords: ['vds', 'dedicated core', '独享物理核', '独享核'] },
                    { path: '/evm/vpc', label: '专有网络 VPC', keywords: ['vpc', 'network'] },
                    { path: '/evm/eip', label: '弹性 IP', keywords: ['eip', 'ip'] },
                    { path: '/evm/security-groups', label: '安全组', keywords: ['security group', 'firewall'] },
                    { path: '/evm/port-forward', label: '端口转发', keywords: ['nat', 'port'] },
                    { path: '/evm/ssh-keys', label: 'SSH 密钥', keywords: ['ssh', 'key', 'keypair', '密钥对', '公钥'] },
                ],
            },
            { path: '/dedicated', label: 'Dedicated Servers', icon: <HddOutlined />, keywords: ['dedicated', 'bare metal', 'server', '独立服务器', '物理机', '裸金属'] },
            { path: '/containers', label: '独享容器', icon: <CodeSandboxOutlined />, keywords: ['container', 'kata', '独享容器', '应用容器', 'podman'] },
            { path: '/host_shared/vhosts', label: '共享主机', icon: <DatabaseOutlined />, keywords: ['vhost', '虚拟主机', 'hosting'] },
        ],
    },
    {
        key: 'network',
        title: '网络与 CDN',
        items: [
            {
                key: 'cdn', label: 'CDN', icon: <ThunderboltOutlined />,
                children: [
                    { path: '/cdn/domains', label: '加速域名', keywords: ['cdn', 'site', '站点'] },
                    { path: '/cdn/analytics', label: '流量分析', keywords: ['traffic', 'analytics'] },
                    { path: '/cdn/workers', label: '边缘函数', keywords: ['workers', 'edge', 'serverless'] },
                ],
            },
            {
                key: 'dns', label: 'DNS 解析', icon: <GlobalOutlined />,
                children: [
                    { path: '/dns/zones', label: '区域管理', keywords: ['dns', 'zone', '解析', 'record'] },
                    { path: '/dns/cidr', label: 'CIDR 管理', keywords: ['cidr', 'ip'] },
                    { path: '/dns/analytics', label: '查询分析', keywords: ['analytics', 'query'] },
                ],
            },
            {
                key: 'domain', label: '域名', icon: <ApartmentOutlined />,
                children: [
                    { path: '/domain-registration/search', label: '注册域名', keywords: ['domain', 'search', 'whois', '域名搜索'] },
                    { path: '/domain-registration/my-domains', label: '我的域名', keywords: ['my domains', '域名列表'] },
                    { path: '/domain-registration/transfer', label: '域名转入', keywords: ['transfer', 'inbound'] },
                    { path: '/domain-registration/contact-templates', label: '域名联系人', keywords: ['contact', 'whois', '联系人', '联系人模板', '通讯录', 'address book'] },
                ],
            },
        ],
    },
    {
        key: 'security',
        title: '安全',
        items: [
            {
                key: 'ssl', label: 'SSL 证书', icon: <SafetyCertificateOutlined />,
                children: [
                    { path: '/ssl/certificates', label: '我的证书', keywords: ['ssl', 'cert', 'tls'] },
                    { path: '/ssl/browse', label: '购买证书', keywords: ['buy', 'ssl', '选购'] },
                    { path: '/ssl/codesigning', label: '代码签名', keywords: ['code signing'] },
                    { path: '/ssl/tools', label: 'SSL 工具', keywords: ['csr', 'tools', 'checker'] },
                ],
            },
        ],
    },
    {
        key: 'sites',
        title: 'Websites',
        items: [
            { path: '/sites', label: 'Sites', icon: <LayoutOutlined />, keywords: ['site builder', 'website', 'shop', 'store', 'ai', 'page', '建站', '网站', '商城', '店铺'] },
        ],
    },
    {
        key: 'storage',
        title: '存储',
        items: [
            { path: '/disk', label: '云盘', icon: <CloudOutlined />, keywords: ['disk', 'drive', 'storage', 'cloud drive', '网盘', '云存储', '存储'] },
            {
                key: 's3', label: 'Object Storage', icon: <InboxOutlined />,
                children: [
                    { path: '/s3', label: 'Buckets', keywords: ['s3', 'object storage', 'bucket', 'r2', '对象存储', '存储桶'] },
                    { path: '/s3/keys', label: 'Access keys', keywords: ['s3', 'access key', 'secret', 'api key', '对象存储', '密钥'] },
                    { path: '/s3/usage', label: 'Usage', keywords: ['s3', 'usage', 'billing', '对象存储', '用量'] },
                ],
            },
        ],
    },
    {
        key: 'mail',
        title: '邮件',
        items: [
            {
                key: 'webmail', label: '域名邮箱', icon: <MailOutlined />,
                children: [
                    { path: '/webmail/domains', label: '邮箱域名', keywords: ['email', 'mail', 'mx'] },
                    { path: '/webmail/users', label: '邮箱用户', keywords: ['mailbox', 'user'] },
                    { path: '/webmail/aliases', label: '邮件别名', keywords: ['alias', 'forward'] },
                    { href: 'https://mail.yeap.cloud/', label: '打开 Webmail ↗', keywords: ['webmail', 'login'] },
                ],
            },
            { path: '/tm/streams', label: '邮件推送', icon: <SendOutlined />, keywords: ['tm', 'smtp', 'transactional', 'marketing', 'send', '发信', '事务邮件', '营销邮件', 'api'] },
        ],
    },
    {
        key: 'ai',
        title: 'AI',
        items: [
            {
                key: 'llm', label: 'LLM API', icon: <RobotOutlined />,
                children: [
                    { path: '/llm/account', label: '账户概览', keywords: ['llm', 'ai', 'gpt'] },
                    { path: '/llm/tokens', label: '令牌管理', keywords: ['key', 'token'] },
                    { path: '/llm/models', label: '模型与定价', keywords: ['models', 'pricing'] },
                    { path: '/llm/logs', label: '调用日志', keywords: ['logs', 'usage'] },
                    { path: '/llm/mj-logs', label: '绘图日志', keywords: ['midjourney', 'image'] },
                    { path: '/llm/task-logs', label: '任务日志', keywords: ['task'] },
                    { path: '/llm/stats', label: '用量统计', keywords: ['stats'] },
                    { path: '/llm/playground', label: 'Playground', keywords: ['chat', 'playground'] },
                    { path: '/llm/subscription', label: 'LLM 套餐', keywords: ['subscription', 'plan'] },
                ],
            },
        ],
    },
    {
        key: 'support',
        title: '支持',
        items: [
            { path: '/support/tickets', label: '工单', icon: <CustomerServiceOutlined />, keywords: ['ticket', 'support', 'help', '工单', '客服', '帮助', '我的工单'] },
        ],
    },
];

/** 右上角账户菜单（费用中心 + 设置），不在侧边栏 */
export const ACCOUNT_MENU = {
    key: 'billing', label: '费用中心', icon: <WalletOutlined />,
    children: [
        { path: '/billing/balance', label: '账户余额', keywords: ['balance', '余额', '充值', 'topup'] },
        { path: '/billing/subscription', label: '订阅管理', keywords: ['subscription', '套餐', '订阅', 'renew'] },
        { path: '/billing/invoices', label: '账单', keywords: ['invoice', '账单', '发票'] },
        { path: '/billing/coupons', label: 'My coupons', keywords: ['coupon', 'promo', 'voucher', 'trial', '优惠券', '优惠码', '代金券'] },
        { path: '/billing/affiliate', label: 'Referral program', keywords: ['affiliate', 'referral', 'invite', 'commission', '推广', '返利', '邀请', '佣金'] },
        { path: '/billing/cart', label: '购物车', keywords: ['cart'] },
    ],
};
export const SETTINGS_PAGE = { path: '/settings', label: '个人设置', icon: <SettingOutlined />, keywords: ['settings', 'profile', 'api key', '2fa', '密钥'] };
export const TRANSFER_PAGE = { path: '/transfers', label: '资源转移', icon: <SwapOutlined />, keywords: ['transfer', 'push', '转移', '过户', '转入', '转出'] };

const CDN_TABS = {
    origin: '回源设置', ssl: 'SSL/TLS', firewall: '防火墙', rules: '页面规则', cache: '缓存',
    optimization: '性能优化', network: '网络', traffic: '流量分析', worker: '边缘函数',
    settings: '域名设置', diagnostics: '诊断', 'error-pages': '错误页面', plan: '套餐',
};
const SITE_SETTINGS_LABELS = { general: 'General', theme: 'Theme', menus: 'Menus', seo: 'SEO', blog: 'Blog', store: 'Store', shipping: 'Shipping', taxes: 'Taxes', redirects: 'Redirects' };
const SITE_MKT_LABELS = { overview: 'Overview', domain: 'Sending domain', campaigns: 'Campaigns', automations: 'Automations', subscribers: 'Subscribers', segments: 'Segments', forms: 'Sign-up forms', suppressions: 'Suppressed' };
const SITE_TAB_LABELS = {
    pages: 'Pages', products: 'Products', collections: 'Collections', orders: 'Orders', returns: 'Returns', customers: 'Customers', discounts: 'Discounts',
    payments: 'Payments', blog: 'Blog', comments: 'Comments', 'blog-terms': 'Categories', 'blog-authors': 'Authors', forms: 'Forms', 'collections-cms': 'Collections', bookings: 'Bookings', media: 'Media', marketing: 'Marketing', staff: 'Staff', 'gift-cards': 'Gift cards', reviews: 'Reviews', inventory: 'Inventory', 'draft-orders': 'Draft orders', abandoned: 'Abandoned carts', domains: 'Domains', settings: 'Settings', versions: 'Versions',
};
const PROVISION_LABELS = {
    host_shared: '共享主机', host: '独享容器', yeapmail: '域名邮箱', webmail: '域名邮箱',
    lightapp: '轻应用', evm: '云服务器', cdn: 'CDN', dns: 'DNS', tm: '邮件推送', disk: '云盘',
};

/**
 * 没有菜单入口的页面。parent = 面包屑上一级 & 侧边栏高亮项。
 * label 可以是函数 (params) => string。palette: true 的页面也出现在 Cmd+K 里。
 * 顺序：更具体的模式在前。
 */
export const EXTRA_PAGES = [
    { pattern: '/dns/records/:zoneId', parent: '/dns/zones', label: '解析记录' },
    { pattern: '/dns/cidr/:cidrId', parent: '/dns/cidr', label: 'CIDR 条目' },
    { pattern: '/dns/authorize', parent: '/dns/zones', label: 'DNS 授权' },
    { pattern: '/cdn/domains/new', parent: '/cdn/domains', label: '添加域名', palette: true, keywords: ['add', 'new', '新建'] },
    { pattern: '/cdn/domains/:domain/:tab', parent: '/cdn/domains/:domain', label: (p) => CDN_TABS[p.tab] || p.tab },
    { pattern: '/cdn/domains/:domain', parent: '/cdn/domains', label: (p) => p.domain },
    { pattern: '/cdn/workers/new', parent: '/cdn/workers', label: () => '创建 Worker' },
    { pattern: '/cdn/workers/:workerId', parent: '/cdn/workers', label: (p) => `Worker #${p.workerId}` },
    { pattern: '/cdn/workers/:workerId/edit', parent: '/cdn/workers/:workerId', label: () => '编辑代码' },
    { pattern: '/domain-registration/my-domains/:id', parent: '/domain-registration/my-domains', label: '域名详情' },
    { pattern: '/ssl/order/:orderId', parent: '/ssl/certificates', label: (p) => t('Certificate order #{{id}}', { id: p.orderId }) },
    { pattern: '/ssl/codesigning-order/:orderId', parent: '/ssl/codesigning', label: (p) => t('Code signing order #{{id}}', { id: p.orderId }) },
    { pattern: '/billing/invoices/:id', parent: '/billing/invoices', label: (p) => t('Invoice #{{id}}', { id: p.id }) },
    { pattern: '/dedicated/order', parent: '/dedicated', label: 'Order a dedicated server', palette: true, keywords: ['dedicated', 'bare metal', '独立服务器'] },
    { pattern: '/dedicated/:id', parent: '/dedicated', label: 'Server details' },
    { pattern: '/containers/create', parent: '/containers', label: '创建独享容器', palette: true, keywords: ['container', 'kata', '独享容器'] },
    { pattern: '/containers/:id', parent: '/containers', label: '容器详情' },
    { pattern: '/containers/legacy/:id', parent: '/containers', label: '容器详情' },
    { pattern: '/host_shared/vhosts/:id', parent: '/host_shared/vhosts', label: '主机详情' },
    { pattern: '/evm/vms/purchase', parent: '/evm/vms', label: '购买实例', palette: true, keywords: ['buy', 'purchase', '购买', '云服务器'] },
    { pattern: '/evm/vms/:id', parent: '/evm/vms', label: '实例详情' },
    { pattern: '/lightapp/services/:id', parent: '/lightapp/services', label: '应用详情' },
    { pattern: '/webmail/plans', parent: '/webmail/domains', label: '邮箱套餐' },
    { pattern: '/cdn/plans', parent: '/cdn/domains', label: 'CDN 套餐' },
    { pattern: '/dns/plans', parent: '/dns/zones', label: 'DNS 套餐' },
    { pattern: '/llm/checkin', parent: '/llm/account', label: '每日签到', palette: true, keywords: ['checkin', '签到'] },
    { pattern: '/provision/:productType', parent: '/dashboard', label: (p) => (PROVISION_LABELS[p.productType] ? t('Activate · {{product}}', { product: t(PROVISION_LABELS[p.productType]) }) : t('Activate service')) },
    { pattern: '/tm/streams/:id', parent: '/tm/streams', label: '发信流详情' },
    { pattern: '/disk/orgs/:id', parent: '/disk', label: '组织' },
    { pattern: '/s3/buckets/:id', parent: '/s3', label: 'Bucket' },
    { pattern: '/sites/:id/:tab/:sub', parent: '/sites/:id/:tab', label: (p) => (p.tab === 'settings' ? t(SITE_SETTINGS_LABELS[p.sub] || p.sub) : p.tab === 'marketing' ? t(SITE_MKT_LABELS[p.sub] || p.sub) : p.sub === 'new' ? t('New') : t('Details')) },
    { pattern: '/sites/:id/:tab', parent: '/sites/:id', label: (p) => t(SITE_TAB_LABELS[p.tab] || p.tab, { context: 'shop' }) },
    { pattern: '/sites/:id', parent: '/sites', label: 'Site details' },
    { pattern: '/support/tickets/new', parent: '/support/tickets', label: '提交工单', palette: true, keywords: ['ticket', 'support', 'new', '新建工单', '客服'] },
    { pattern: '/support/tickets/:id', parent: '/support/tickets', label: '工单详情' },
];

// ---------------------------------------------------------------------------
// 派生
// ---------------------------------------------------------------------------

const leaves = [];
NAV_SECTIONS.forEach((section) => {
    section.items.forEach((item) => {
        if (item.children) {
            item.children.forEach((c) => leaves.push({ ...c, sub: item, section }));
        } else {
            leaves.push({ ...item, sub: null, section });
        }
    });
});
ACCOUNT_MENU.children.forEach((c) => leaves.push({ ...c, sub: ACCOUNT_MENU, section: { key: 'account', title: '费用中心' } }));
leaves.push({ ...SETTINGS_PAGE, sub: null, section: { key: 'account', title: '账户' } });
leaves.push({ ...TRANSFER_PAGE, sub: null, section: { key: 'account', title: '账户' } });

export const ALL_LEAVES = leaves.filter((l) => l.path);

const compile = (pattern) => {
    const names = [];
    const re = new RegExp('^' + pattern.replace(/:[A-Za-z]+/g, (m) => { names.push(m.slice(1)); return '([^/]+)'; }) + '/?$');
    return { re, names };
};
const COMPILED = EXTRA_PAGES.map((p) => ({ ...p, ...compile(p.pattern) }));

const fill = (pattern, params) => pattern.replace(/:([A-Za-z]+)/g, (_, k) => encodeURIComponent(params[k] ?? ''));

const findLeaf = (pathname) => {
    let best = null;
    ALL_LEAVES.forEach((l) => {
        if (pathname === l.path || pathname.startsWith(l.path + '/')) {
            if (!best || l.path.length > best.path.length) best = l;
        }
    });
    return best;
};

/**
 * 解析当前路径 → { leaf（侧边栏高亮项）, crumbs: [{label, path?}] }
 */
export const resolveRoute = (rawPathname) => {
    let pathname = (rawPathname || '/').replace(/\/+$/, '') || '/';
    if (pathname.startsWith('/user/')) pathname = pathname.slice(5); // combined 构建兼容

    for (const page of COMPILED) {
        const m = pathname.match(page.re);
        if (!m) continue;
        const params = {};
        page.names.forEach((n, i) => { params[n] = decodeURIComponent(m[i + 1]); });
        const parentPath = fill(page.parent, params);
        const parent = resolveRoute(parentPath);
        const label = typeof page.label === 'function' ? page.label(params) : page.label;
        return { leaf: parent.leaf, crumbs: [...parent.crumbs.map((c, i, arr) => (i === arr.length - 1 && !c.path ? { ...c, path: parentPath } : c)), { label }] };
    }

    const leaf = findLeaf(pathname);
    if (!leaf) return { leaf: null, crumbs: [] };
    const crumbs = [];
    if (leaf.sub) {
        const first = leaf.sub.children.find((c) => c.path);
        crumbs.push({ label: leaf.sub.label, path: first && first.path !== leaf.path ? first.path : undefined });
    }
    crumbs.push({ label: leaf.label, path: leaf.path === pathname ? undefined : leaf.path });
    return { leaf, crumbs };
};

/** Cmd+K 命令列表 */
export const PALETTE_COMMANDS = [
    ...ALL_LEAVES.map((l) => ({
        key: l.path,
        label: l.label,
        subLabel: l.sub && l.sub !== ACCOUNT_MENU ? l.sub.label : undefined,
        section: l.section.title || (l.sub ? l.sub.label : '导航'),
        path: l.path,
        icon: l.icon || l.sub?.icon,
        keywords: [l.label, l.sub?.label, ...(l.keywords || [])].filter(Boolean),
    })),
    ...EXTRA_PAGES.filter((p) => p.palette).map((p) => {
        const parentLeaf = findLeaf(p.parent);
        return {
            key: p.pattern,
            label: p.label,
            section: parentLeaf?.section?.title || '导航',
            path: p.pattern,
            icon: parentLeaf?.icon || parentLeaf?.sub?.icon,
            keywords: [p.label, ...(p.keywords || [])],
        };
    }),
];
